import { Form, FormControl, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import { useCaseActivitySheriffFileMutation } from "@/features/case-evaluation/api/queries/acitivities-query";
import {
	caseActivityNoteQueryOptions,
	useActivityNoteDeleteMutation,
	useActivityNoteMutation,
} from "@/features/case-evaluation/api/queries/activity-notes-query";
import type { CaseActivity } from "@/features/case-evaluation/types/activities";
import { type ActivityNoteFormSchema, activityNoteFormSchema } from "@/features/case-evaluation/types/activity-notes";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import userService from "@/features/dashboard/api/services/user-service";
import { useAuth } from "@/hooks/use-auth";
import { queryClient } from "@/query-client";
import { formatDate } from "@/utils/format-date";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconDots, IconEye, IconFile, IconMoodLookDown, IconPaperclip, IconSend2, IconX } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from "../core/actions/dropdown-menu";
import { Blanket, BlanketImage, BlanketInfo } from "../core/blanket";
import { Button } from "../core/buttons/button";
import { FileLinkWithViewer } from "../core/file-link-with-viewer";
import DragDropFile from "../core/forms/drag-drop-file";
import { Input } from "../core/forms/input";
import TagsInput from "../core/forms/tags-input";
import { Textarea } from "../core/forms/textarea";
import { useModal } from "../core/modals/use-modal";
import { useModalControl } from "../core/modals/use-modal-controller";
import Tooltip from "../core/tooltip";

export default function CaseActivityNoteModal({
	caseRecord,
	activity,
	branchId,
}: {
	caseRecord: CaseRecord;
	activity: CaseActivity;
	branchId: string;
}) {
	const notesQuery = useSuspenseQuery(
		caseActivityNoteQueryOptions({
			case_activity_id: activity.id,
		}),
	);

	const notes = notesQuery.data?.data;
	const { user } = useAuth();
	const { closeModal } = useModalControl();
	const modal = useModal();

	const inputRef = useRef<HTMLInputElement | null>(null);
	const [inputValue, setInputValue] = useState(null);
	// const [files, setFiles] = useState<File[]>([]);

	const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.value;
		setInputValue(newValue);
	};

	const [multipleFiles, setMultipleFiles] = useState<File[]>([]);
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const activityNoteMutation = useActivityNoteMutation(caseRecord.id);
	const caseActivitiesNoteDeleteMutation = useActivityNoteDeleteMutation(caseRecord.id);

	const form = useForm<ActivityNoteFormSchema>({
		resolver: zodResolver(activityNoteFormSchema),
		mode: "onChange",
		defaultValues: {
			id: null,
			case_activity_id: activity.id,
			case_record_id: caseRecord.id,
			note: "",
			share_with: null,
			attachment: null,
		},
	});

	const handleSubmit = async (values: any) => {
		await activityNoteMutation.mutateAsync(values);
		form.setValue("note", "");
		form.setValue("attachment", null);
		form.setValue("share_with", []);
		setMultipleFiles([]);
	};

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (files && files.length > 0) {
			const objectUrl = URL.createObjectURL(files[0]);
			setSelectedFiles((prevFiles) => {
				const newFiles = [...prevFiles, ...Array.from(files)];
				form.setValue("attachment", newFiles);
				setMultipleFiles(newFiles);
				return newFiles;
			});
		}
	};

	const handleEditNote = (note: any) => {
		form.setValue("id", note.id);
		form.setValue("note", note.note);
		form.setValue("case_activity_id", note.case_activity_id);
		form.setValue("case_record_id", note.case_record_id);
		form.setValue("share_with", note.share_with || []);
		setMultipleFiles(note.medias.map((media: any) => new File([media], media.name)));
		if (inputRef.current) {
			inputRef.current.value = note.to;
		}
	};

	const files = multipleFiles.map((file) => (
		<div
			key={file.name}
			className="cursor-pointer inline-flex flex-row items-center gap-1 p-1 w-auto rounded-md whitespace-nowrap bg-blue-50 hover:bg-blue-100"
		>
			<span className="inline-flex text-blue-600">
				<IconFile size={14} stroke={1.5} />
			</span>
			<span className="text-sm font-semibold text-blue-600">{file.name}</span>
			<Button
				variant="link"
				size="xs"
				className="!p-0"
				// onClick={}
			>
				<IconX size={14} stroke={2.5} />
			</Button>
		</div>
	));

	return (
		<Form {...form}>
			<form className="flex flex-col w-full gap-5" onSubmit={form.handleSubmit(handleSubmit)}>
				<div className="flex flex-col w-full gap-5">
					<div className="flex flex-row items-center justify-between w-full">
						<span className="text-sm font-semibold">Activity Comments</span>
					</div>
					{notes.length !== 0 ? (
						<>
							{notes.map((note) => (
								<div key={note.id} className="flex flex-row gap-2">
									<div className="flex flex-row gap-2 w-full">
										<div className="flex items-start justify-start">
											<span className="flex h-[40px] w-[40px] rounded-full bg-primary-600 items-center justify-center text-white font-semibold shrink-0">
												{note.user.charAt(0).toUpperCase()}
											</span>
										</div>
										<div className="flex flex-col gap-2">
											<div className="flex flex-col">
												<span className="text-base font-semibold">{note.user}</span>
												<div className="inline-flex gap-1 items-center">
													<span className="text-sm text-foreground">To</span>
													<span className="text-sm font-semibold text-blue-600">
														{note.share_with?.length > 0 ? note.share_with.map((user: any) => user.label).join(", ") : "All"}
													</span>
													<span className="text-sm text-foreground">• {formatDate(note.updated_at, "PPp")}</span>
												</div>
											</div>
											<div className="text-base">{note.note}</div>
											<div>
												{note.medias.length !== 0 && (
													<span className="flex flex-row flex-wrap gap-1.5">
														{note.medias.map((file, index) => (
															<FileLinkWithViewer key={index} file={file} url={file.original_url} maxLength={20} mime_type={""} />
														))}
													</span>
												)}
											</div>
										</div>
									</div>
									{note.user_id === user.id && (
										<DropdownMenu modal={false}>
											<DropdownMenuTrigger asChild>
												<Button className="!rounded-full shrink-0" type="button" size="icon" variant="ghost">
													<IconDots size={14} stroke={2} />
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent>
												<DropdownMenuGroup>
													<DropdownMenuItem
														onClick={() => {
															handleEditNote(note);
														}}
														className="cursor-pointer"
													>
														<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
															<span>Edit</span>
														</div>
													</DropdownMenuItem>

													<DropdownMenuItem
														onClick={() => {
															const modalId = modal.openModal({
																type: "confirm",
																variant: "red",
																title: "Delete Note",
																onConfirm: async () => {
																	try {
																		await caseActivitiesNoteDeleteMutation.mutateAsync(note.id);
																		modal.closeModal(modalId);
																	} catch (error) {}
																	modal.closeModal(modalId);
																},
															});
														}}
														dangerZone
														className="cursor-pointer"
													>
														<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
															<span>Delete</span>
														</div>
													</DropdownMenuItem>
												</DropdownMenuGroup>
											</DropdownMenuContent>
										</DropdownMenu>
									)}
								</div>
								// <div key={note.id} className="flex flex-col w-full p-3 border border-border rounded-md bg-background">
								// 	<div className="flex flex-row items-center justify-between">
								// 		<span className="text-sm font-semibold text-foreground">{note.user}</span>
								// 		<span className="text-xs text-foreground/50">{formatDate(note.created_at, "PPp")}</span>
								// 	</div>
								// 	<p className="mt-2 text-sm text-foreground-dark">{note.note}</p>

								// 	{note.medias.length !== 0 && (
								// 		<div className="flex flex-row flex-wrap gap-1.5 mt-2">
								// 			{note.medias.map((file, index) => (
								// 				<FileLinkWithViewer key={index} file={file} url={file.original_url} maxLength={20} mime_type={""} />
								// 			))}
								// 		</div>
								// 	)}
								// </div>
							))}
						</>
					) : (
						<Blanket>
							<BlanketImage icon={<IconMoodLookDown size={30} stroke={1.5} />} />
							<BlanketInfo title="No Comments" message="No available comments for for now. Please check back later or refresh the page." />
						</Blanket>
					)}
				</div>

				<div className="border-t border-border" />

				<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
					{/* <label className="block text-sm font-semibold text-gray-700">To:</label> */}
					<div className="col-span-4">
						<FormField
							control={form.control}
							name="share_with"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>To:</FormLabel>
									<FormGroup>
										<FormControl>
											<TagsInput
												isMulti={true}
												defaultValue={field.value}
												onValueChange={(value) => {
													field.onChange(value);
												}}
												placeholder={form.watch("share_with")?.length > 0 ? form.watch("share_with")[0].label : "Select Recipient"}
												className="flex w-full h-full !border-0 !outline-none !focus:outline-none !focus:border-0 focus:ring-0 p-0 text-base text-foreground"
												loadOptions={async (value: string) => {
													const items = await userService.listByBranch(branchId);

													const allOption = {
														label: "All",
														value: "all",
													};

													const userOptions =
														items?.map((item) => ({
															label: `${item.full_name}`,
															value: item.id,
														})) || [];

													return [allOption, ...userOptions];
												}}
											/>
										</FormControl>
									</FormGroup>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					{/* <label className="block text-sm font-semibold text-gray-700">Body</label> */}
					<div className="col-span-12">
						<FormField
							control={form.control}
							name="note"
							render={({ field }) => (
								<FormItem>
									<FormGroup>
										{/* <Input
                      className="flex w-full h-full !border-0 !outline-none !focus:outline-none !focus:border-0 focus:ring-0 p-0 text-base text-foreground"
                      {...field}
                    /> */}
										<div className="flex flex-row items-center w-full gap-1">
											<div className="flex flex-row items-start w-full min-h-[38px] border border-border rounded-md overflow-hidden px-3 py-2">
												<div className="flex flex-col w-full justify-center gap-3">
													<input
														placeholder="Say something..."
														className="flex w-full h-full !border-0 !outline-none !focus:outline-none !focus:border-0 focus:ring-0 p-0 text-base text-foreground"
														{...field}
													/>
													{files.length > 0 && <span className="flex flex-row flex-wrap gap-1.5">{files}</span>}
												</div>
												<div className="inline-flex justify-center items-center shrink-0 gap-1">
													<Tooltip className="z-[999]" content="Upload File" side="bottom">
														<button type="button" className="text-primary-600" onClick={() => inputRef.current?.click()}>
															<IconPaperclip size={18} stroke={1.5} />
														</button>
													</Tooltip>
													<input
														type="file"
														accept="application/pdf, image/*"
														ref={inputRef}
														className="absolute inset-0 w-0 h-full cursor-pointer opacity-0"
														multiple
														onChange={handleFileChange}
														tabIndex={-1}
													/>
													{form.watch("note") && (
														<div className="inline-flex justify-center items-center shrink-0">
															<button type="submit" disabled={form.formState.isSubmitting} className="text-primary-500">
																<IconSend2 size={18} stroke={1.5} />
															</button>
														</div>
													)}
												</div>
											</div>
										</div>
									</FormGroup>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</div>
			</form>
		</Form>
	);
}
